import { useState } from 'react';
import { paymentConfig, type PaymentConfig } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, CreditCard, Eye, EyeOff } from 'lucide-react';

const paymentFields: { key: keyof PaymentConfig; label: string; placeholder: string; type?: string }[] = [
  { key: 'stripePublicKey', label: 'Stripe Public Key', placeholder: 'pk_test_...' },
  { key: 'stripeSecretKey', label: 'Stripe Secret Key', placeholder: 'sk_test_...' },
  { key: 'paypalClientId', label: 'PayPal Client ID', placeholder: 'Enter PayPal client ID' },
  { key: 'paypalSecret', label: 'PayPal Secret', placeholder: 'Enter PayPal secret' },
  { key: 'squareApplicationId', label: 'Square Application ID', placeholder: 'sandbox-...' },
  { key: 'squareAccessToken', label: 'Square Access Token', placeholder: 'EAAA...' },
];

export default function Payments() {
  const [config, setConfig] = useState<PaymentConfig>(paymentConfig.get());
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});

  const handleChange = (key: keyof PaymentConfig, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    paymentConfig.set(config);
    toast.success('Payment configuration saved successfully');
  };

  const isActive = (key: keyof PaymentConfig) => !!config[key];

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">Payment Configuration</h1>
        <p className="text-white/60 text-sm mt-1">Configure Stripe, PayPal, and Square payment gateways.</p>
      </div>

      {/* Stripe */}
      <div className="card-dark p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#635BFF]/20 text-[#635BFF]">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white">Stripe</h3>
            <p className="text-xs text-white/70">Card payments</p>
          </div>
          <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${
            isActive('stripePublicKey') && isActive('stripeSecretKey')
              ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-white/5 text-white/60'
          }`}>
            {isActive('stripePublicKey') && isActive('stripeSecretKey') ? 'Active' : 'Not Configured'}
          </span>
        </div>
        <div className="space-y-4">
          {paymentFields.filter(f => f.key.includes('stripe')).map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-2">
              <Label className="text-white/80 text-sm">{label}</Label>
              <div className="relative">
                <Input
                  type={showKeys[key] ? 'text' : 'password'}
                  value={config[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  className="h-11 bg-[#0057B7] border-white/10 text-white pr-10"
                />
                <button type="button" onClick={() => setShowKeys(p => ({ ...p, [key]: !p[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/60">
                  {showKeys[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* PayPal */}
      <div className="card-dark p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#003087]/20 text-[#0070E0]">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white">PayPal</h3>
            <p className="text-xs text-white/70">PayPal checkout</p>
          </div>
          <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${
            isActive('paypalClientId') && isActive('paypalSecret')
              ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-white/5 text-white/60'
          }`}>
            {isActive('paypalClientId') && isActive('paypalSecret') ? 'Active' : 'Not Configured'}
          </span>
        </div>
        <div className="space-y-4">
          {paymentFields.filter(f => f.key.includes('paypal')).map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-2">
              <Label className="text-white/80 text-sm">{label}</Label>
              <div className="relative">
                <Input
                  type={showKeys[key] ? 'text' : 'password'}
                  value={config[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  className="h-11 bg-[#0057B7] border-white/10 text-white pr-10"
                />
                <button type="button" onClick={() => setShowKeys(p => ({ ...p, [key]: !p[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/60">
                  {showKeys[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Square */}
      <div className="card-dark p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00A86B]/20 text-[#00A86B]">
            <CreditCard className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-white">Square</h3>
            <p className="text-xs text-white/70">In-person + online payments</p>
          </div>
          <span className={`ml-auto text-xs px-2.5 py-1 rounded-full font-medium ${
            isActive('squareApplicationId') && isActive('squareAccessToken')
              ? 'bg-[#22c55e]/10 text-[#22c55e]' : 'bg-white/5 text-white/60'
          }`}>
            {isActive('squareApplicationId') && isActive('squareAccessToken') ? 'Active' : 'Not Configured'}
          </span>
        </div>
        <div className="space-y-4">
          {paymentFields.filter(f => f.key.includes('square')).map(({ key, label, placeholder }) => (
            <div key={key} className="space-y-2">
              <Label className="text-white/80 text-sm">{label}</Label>
              <div className="relative">
                <Input
                  type={showKeys[key] ? 'text' : 'password'}
                  value={config[key]}
                  onChange={(e) => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  className="h-11 bg-[#0057B7] border-white/10 text-white pr-10"
                />
                <button type="button" onClick={() => setShowKeys(p => ({ ...p, [key]: !p[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/60">
                  {showKeys[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <Label className="text-white/80 text-sm">Environment</Label>
          <div className="flex gap-3">
            {(['sandbox', 'production'] as const).map((env) => (
              <label key={env} className={`cursor-pointer px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                config.squareEnvironment === env
                  ? 'border-[#FFDD00] bg-[#FFDD00]/10 text-[#0057B7]'
                  : 'border-white/10 text-white/60 hover:border-white/30'
              }`}>
                <input
                  type="radio"
                  name="squareEnv"
                  value={env}
                  checked={config.squareEnvironment === env}
                  onChange={() => setConfig(p => ({ ...p, squareEnvironment: env }))}
                  className="sr-only"
                />
                {env.charAt(0).toUpperCase() + env.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={handleSave} className="btn-primary">
        <Save className="w-4 h-4" />
        Save Payment Configuration
      </Button>
    </div>
  );
}